// Page-type catalog for the Onboarding Constructor.
//
// One catalog entry per page in the bundled default config. The default config
// happens to contain every renderable ScreenType and every field variant, so
// deriving the palette from it guarantees added pages carry real options and a
// structure the iOS decoder accepts. (Fields the iOS model can't render — e.g.
// dinner_time / weighin_time — are intentionally absent and so never offered.)
import defaultPages from './defaultPages.json';

export const CATEGORIES = [
  'Profile basics',
  'Body & metrics',
  'Goals & preferences',
  'Aha & charts',
  'Social proof',
  'System & paywall',
];

// field → { label, category, emoji, repeatable? }
const FIELD_META = {
  // Profile basics
  name: { label: 'Name', category: 'Profile basics', emoji: '🪪' },
  welcome_personalized: { label: 'Welcome', category: 'Profile basics', emoji: '👋' },
  gender: { label: 'Gender', category: 'Profile basics', emoji: '⚧️' },
  motivation: { label: 'Motivation', category: 'Profile basics', emoji: '🔥' },
  mini_quiz: { label: 'Mini Quiz', category: 'Profile basics', emoji: '❓' },
  photo_demo: { label: 'Photo Demo', category: 'Profile basics', emoji: '🎬' },

  // Body & metrics
  unit: { label: 'Units', category: 'Body & metrics', emoji: '📐' },
  weight: { label: 'Weight', category: 'Body & metrics', emoji: '⚖️' },
  height: { label: 'Height', category: 'Body & metrics', emoji: '📏' },
  age: { label: 'Age', category: 'Body & metrics', emoji: '🎂' },
  apple_health: { label: 'Apple Health', category: 'Body & metrics', emoji: '❤️' },

  // Goals & preferences
  goal: { label: 'Goal', category: 'Goals & preferences', emoji: '🎯' },
  new_goal: { label: 'Target Weight', category: 'Goals & preferences', emoji: '🏁' },
  weekly_goal: { label: 'Weekly Goal', category: 'Goals & preferences', emoji: '📅' },
  photo_frequency: { label: 'Photo Frequency', category: 'Goals & preferences', emoji: '📷' },
  lactose: { label: 'Lactose', category: 'Goals & preferences', emoji: '🥛' },
  accomplish: { label: 'Accomplish', category: 'Goals & preferences', emoji: '✨' },
  fitness_level: { label: 'Fitness Level', category: 'Goals & preferences', emoji: '🏋️' },
  activity_level: { label: 'Activity Level', category: 'Goals & preferences', emoji: '🏃' },
  training_type: { label: 'Training Type', category: 'Goals & preferences', emoji: '🤸' },

  // Aha & charts
  aha_compare_2x: { label: 'Aha · 2× Faster', category: 'Aha & charts', emoji: '📊' },
  aha_realistic: { label: 'Aha · Realistic', category: 'Aha & charts', emoji: '📈' },
  aha_even_sooner: { label: 'Aha · Even Sooner', category: 'Aha & charts', emoji: '⚡' },
  plan_reveal: { label: 'Plan Reveal', category: 'Aha & charts', emoji: '🍩' },

  // Social proof
  review: { label: 'Reviews', category: 'Social proof', emoji: '⭐' },
  referral_source: { label: 'Referral Source', category: 'Social proof', emoji: '📣' },

  // System & paywall
  notpermission: { label: 'Notifications', category: 'System & paywall', emoji: '🔔' },
  promo_code: { label: 'Promo Code', category: 'System & paywall', emoji: '🎟️' },
  setting_up: { label: 'Setting Up', category: 'System & paywall', emoji: '⏳', repeatable: true },
  prepurchase: { label: 'Prepurchase', category: 'System & paywall', emoji: '🛒', repeatable: true },
};

function prettify(field) {
  return String(field)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function clonePage(page) {
  return JSON.parse(JSON.stringify(page));
}

export const CATALOG = defaultPages.map((page) => {
  const field = page.field;
  const meta = FIELD_META[field] || {
    label: prettify(field),
    category: 'System & paywall',
    emoji: '📄',
  };
  return {
    id: field,
    field,
    type: page.content && page.content.type,
    label: meta.label,
    category: meta.category,
    emoji: meta.emoji,
    repeatable: !!meta.repeatable,
    makeDefault: () => clonePage(page),
  };
});

const BY_FIELD = Object.fromEntries(CATALOG.map((e) => [e.field, e]));

// Build a fresh page object for a given field id (deep-cloned default).
export function makePage(field) {
  const entry = BY_FIELD[field];
  return entry ? entry.makeDefault() : null;
}

// Catalog metadata for a field (label/emoji/category), with a graceful fallback
// so imported pages with unknown fields still display something sensible.
export function metaForField(field) {
  return (
    BY_FIELD[field] || {
      id: field,
      field,
      label: prettify(field),
      emoji: '📄',
      category: 'System & paywall',
      repeatable: false,
    }
  );
}

export { defaultPages };
